import type { InputHTMLAttributes } from 'react'
import { forwardRef, useEffect, useRef } from 'react'

import { cn } from '@/lib/cn'

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  /** The "some but not all" state of a select-all box. */
  indeterminate?: boolean
}

/**
 * A native checkbox, styled.
 *
 * `indeterminate` is a DOM property with no HTML attribute behind it, so it has to be set
 * through a ref — which is the whole reason this is a component rather than a class name
 * on an `<input>`. The caller's ref is kept too: react-hook-form's `register` hands one in,
 * and swallowing it would leave the field unregistered.
 */
export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  { indeterminate = false, className, ...props },
  forwardedRef,
) {
  const inner = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    if (inner.current) inner.current.indeterminate = indeterminate
  }, [indeterminate])

  return (
    <input
      ref={(node) => {
        inner.current = node
        if (typeof forwardedRef === 'function') forwardedRef(node)
        else if (forwardedRef) forwardedRef.current = node
      }}
      type="checkbox"
      className={cn(
        'h-4 w-4 rounded border-slate-300 text-brand-600 accent-brand-600',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2',
        'disabled:cursor-not-allowed disabled:opacity-50',
        'dark:border-slate-600 dark:bg-slate-900 dark:ring-offset-slate-900',
        className,
      )}
      {...props}
    />
  )
})
